import { ApolloError } from '@apollo/client';
import MainLayout from '@layouts/main';
import { signIn, useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { Component, FC, PropsWithChildren, useEffect } from 'react';

function is401Error(error: Error): boolean {
  return (
    error instanceof ApolloError &&
    // @ts-expect-error
    error.networkError?.statusCode === 401
  );
}

export class AuthBoundary extends Component<PropsWithChildren> {
  declare state: { error?: Error };

  constructor(props: PropsWithChildren) {
    super(props);
    this.state = {};
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI.
    return { error };
  }

  componentDidCatch(error: Error): void {
    if (is401Error(error)) {
      // If the error is an ApolloError with a 401 status code, we can assume
      // that the user is not authenticated and redirect them to the login page.
      console.log('AuthBoundary: Redirecting to login due to 401 error');
      signIn('da', { callbackUrl: window.location.href });
    }
  }

  render() {
    if (this.state.error != null) {
      if (is401Error(this.state.error)) {
        return (
          <MainLayout
            alignment="top"
            panelled={false}
            restrictWidth={true}
            showSpinner
            noAuth
          />
        );
      } else {
        throw this.state.error;
      }
    }

    return (
      <>
        <AuthHandler />
        {this.props.children}
      </>
    );
  }
}

const AuthHandler: FC = () => {
  const router = useRouter();
  const { data: session, status } = useSession();

  useEffect(() => {
    if (router.asPath.includes('/auth')) return;

    if (status === 'loading' || status === 'authenticated') return;

    signIn('da', { callbackUrl: router.asPath }); // Force sign in to hopefully resolve error
  }, [router.asPath, session, status]);

  return null;
};
